import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Minus, Plus, ShieldCheck, ShoppingBag, Truck, RotateCcw, Zap, FileText, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { catUrl } from "../../lib/categoryUrl";
import { useStore } from "../../context/StoreContext";
import { fmtTRY } from "../../lib/format";
import ProductCard from "../../components/storefront/ProductCard";
import QuoteModal from "../../components/storefront/QuoteModal";
import { Reveal } from "../../components/storefront/Reveal";


export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, setCartOpen } = useStore();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selection, setSelection] = useState({});
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [installments, setInstallments] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [customPhoto, setCustomPhoto] = useState(null);
  const [customText, setCustomText] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setProduct(null); setSelection({}); setQty(1); setActiveImage(0); setNotFound(false);
    api.get(`/products/${slug}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setNotFound(true));
    window.scrollTo({ top: 0 });
  }, [slug]);

  const options = product?.variant_options || [];
  const variants = product?.variants || [];

  const selectedVariant = useMemo(() => {
    if (!product?.has_variants) return null;
    if (Object.keys(selection).length < options.length) return null;
    return variants.find((v) =>
      options.every((o) => v.option_values?.[o.name] === selection[o.name])) || null;
  }, [selection, product, options, variants]);

  const optionAvailable = (optName, value) => {
    return variants.some((v) =>
      v.option_values?.[optName] === value &&
      options.every((o) => o.name === optName || !selection[o.name] || v.option_values?.[o.name] === selection[o.name]) &&
      v.stock > 0);
  };

  const currentStock = product?.has_variants ? (selectedVariant?.stock ?? 0) : product?.stock ?? 0;
  const stockThreshold = selectedVariant?.low_stock_threshold ?? product?.critical_stock ?? 5;
  const unitPrice = product?.has_variants
    ? (selectedVariant ? (selectedVariant.sale_price ?? selectedVariant.price ?? product.display_price) : product.display_price)
    : product?.display_price;
  const listPrice = product?.has_variants
    ? (selectedVariant?.price ?? product.list_price)
    : product?.list_price;
  const variantImages = selectedVariant?.images?.length ? selectedVariant.images
    : selectedVariant?.image_url ? [selectedVariant.image_url] : null;
  const images = variantImages || (product?.images?.length ? product.images : []);
  const currentBarcode = selectedVariant?.barcode || product?.barcode;

  const canBuy = product && (!product.has_variants || selectedVariant) && currentStock > 0;

  useEffect(() => {
    if (unitPrice && !product?.requires_quote) {
      api.get(`/payments/installments?amount=${unitPrice}`).then(({ data }) => setInstallments(data)).catch(() => {});
    }
  }, [unitPrice]);

  const handleCustomUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/customization", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCustomPhoto(data.url);
      toast.success("Fotoğraf yüklendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fotoğraf yüklenemedi");
    } finally {
      setUploading(false);
    }
  };

  const handleAdd = async (thenCheckout) => {
    if (!canBuy) {
      toast.error(product?.has_variants && !selectedVariant ? "Lütfen tüm varyantları seçin" : "Bu ürün tükendi");
      return;
    }
    let customization = null;
    if (product.customizable && (customPhoto || customText.trim())) {
      customization = {};
      if (customPhoto) customization.photo_url = customPhoto;
      if (customText.trim()) customization.text = customText.trim();
    }
    const ok = await addToCart(product.product_id, selectedVariant?.variant_id || null, qty, customization);
    if (ok && thenCheckout) { setCartOpen(false); navigate("/odeme"); }
  };

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-32 text-center">
        <h1 className="font-display font-bold text-2xl mb-3">Ürün bulunamadı</h1>
        <Link to="/ara" className="text-amber-700 font-semibold underline" data-testid="pdp-not-found-browse">Tüm ürünlere göz atın</Link>
      </div>
    );
  }

  if (!product) {
    return <div className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-2 gap-12">
      <div className="aspect-[4/5] bg-[#F4F2EB] rounded-3xl animate-pulse-soft" />
      <div className="space-y-4"><div className="h-8 bg-[#F4F2EB] rounded w-2/3" /><div className="h-6 bg-[#F4F2EB] rounded w-1/3" /><div className="h-40 bg-[#F4F2EB] rounded" /></div>
    </div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <Helmet>
        <title>{product.seo_title || `${product.name} | BIGELDİ`}</title>
        <meta name="description" content={product.seo_description || product.short_description || product.name} />
        {product.seo_keywords && <meta name="keywords" content={product.seo_keywords} />}
        <link rel="canonical" href={`${window.location.origin}/urun/${product.slug}`} />
      </Helmet>

      <nav className="text-xs text-neutral-400 mb-8 flex items-center gap-2 flex-wrap" data-testid="pdp-breadcrumb">
        <Link to="/" className="hover:text-amber-700">Anasayfa</Link><span>/</span>
        {product.category && <><Link to={catUrl(product.category)} className="hover:text-amber-700">{product.category.name}</Link><span>/</span></>}
        {product.subcategory && <><Link to={catUrl(product.subcategory, product.category?.slug)} className="hover:text-amber-700">{product.subcategory.name}</Link><span>/</span></>}
        <span className="text-neutral-600">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-[#F4F2EB] border border-[#E6E4DD]" data-testid="pdp-gallery">
            {images[activeImage] && <img src={images[activeImage]} alt={product.name} className="w-full h-full object-cover" />}
            {unitPrice < listPrice && (
              <span className="absolute top-4 left-4 bg-rose-600 text-white text-xs font-bold px-3 py-1.5 rounded-full" data-testid="pdp-discount-badge">
                %{Math.round((1 - unitPrice / listPrice) * 100)} İndirim
              </span>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImage(i)}
                  className={`w-20 h-24 rounded-xl overflow-hidden border-2 transition-colors ${i === activeImage ? "border-amber-600" : "border-transparent"}`}
                  data-testid={`pdp-thumb-${i}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}>
          {product.brand && <Link to={`/marka/${product.brand.slug}`} className="font-mono-ed text-[11px] uppercase tracking-[0.25em] text-amber-700 hover:underline" data-testid="pdp-brand">{product.brand.name}</Link>}
          <h1 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight mt-2 text-balance" data-testid="pdp-title">{product.name}</h1>
          <p className="font-mono-ed text-[11px] text-neutral-400 mt-2" data-testid="pdp-sku">
            SKU: {selectedVariant?.sku || product.sku}{currentBarcode ? ` · Barkod: ${currentBarcode}` : ""}
          </p>

          {product.requires_quote ? (
            <div className="mt-6 bg-white border border-amber-200 rounded-2xl p-5" data-testid="pdp-quote-info">
              <p className="font-display font-bold text-lg">Bu ürün teklif ile satılmaktadır</p>
              <p className="text-sm text-neutral-500 mt-1">Projenize özel fiyat almak için teklif talebi oluşturun.</p>
            </div>
          ) : (
          <>
          <div className="flex items-baseline gap-3 mt-6" data-testid="pdp-price">
            <span className="font-mono-ed font-bold text-3xl sm:text-4xl tracking-tight">{fmtTRY(unitPrice)}</span>
            {unitPrice < listPrice && <span className="text-lg text-neutral-400 line-through">{fmtTRY(listPrice)}</span>}
          </div>
          {product.campaign_name && <p className="text-sm text-amber-700 font-semibold mt-1.5">{product.campaign_name} kampanyası uygulandı</p>}
          {installments && !installments.is_test && installments.options?.length > 1 && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="pdp-installment-badge">
              Garanti BBVA karta {Math.max(...installments.options.map((o) => o.count))} taksit imkânı
            </p>
          )}
          <p className="text-[11px] text-neutral-400 mt-2">Fiyatlara KDV (%{product.vat_rate}) dahildir. Taksit seçenekleri ödeme adımında sunulur.</p>
          </>
          )}
          {installments?.options?.length > 1 && (
            <div className="mt-3 bg-white border border-[#E6E4DD] rounded-2xl p-4" data-testid="pdp-installments">
              <div className="flex items-center justify-between mb-2.5">
                <p className="font-mono-ed text-[10px] uppercase tracking-[0.2em] text-neutral-400">Taksit Seçenekleri ({installments.provider === "garanti" ? "Garanti BBVA" : "Ödeme Sağlayıcı"})</p>
                {installments.is_test && <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid="installment-test-badge">TEST VERİSİ</span>}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                {installments.options.map((o) => (
                  <div key={o.count} className="text-center border border-[#E6E4DD] rounded-lg py-2 px-1">
                    <p className="text-xs font-bold">{o.count === 1 ? "Peşin" : `${o.count} Taksit`}</p>
                    <p className="text-[10px] text-neutral-500 font-mono-ed">{o.count === 1 ? fmtTRY(o.total) : `${o.count} × ${fmtTRY(o.monthly)}`}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.has_variants && (
            <div className="mt-8 space-y-5" data-testid="variant-selector">
              {options.filter((opt) => Array.isArray(opt?.values)).map((opt) => (
                <div key={opt.name}>
                  <p className="text-sm font-semibold mb-2.5">{opt.name}: <span className="text-neutral-500 font-normal">{selection[opt.name] || "Seçin"}</span></p>
                  <div className="flex flex-wrap gap-2">
                    {opt.values.map((value) => {
                      const available = optionAvailable(opt.name, value);
                      const selected = selection[opt.name] === value;
                      return (
                        <button key={value}
                          onClick={() => setSelection((s) => ({ ...s, [opt.name]: value }))}
                          disabled={!available}
                          className={`px-4 h-11 rounded-xl text-sm font-medium border-2 transition-all
                            ${selected ? "border-[#121214] bg-[#121214] text-white" : available ? "border-[#E6E4DD] bg-white hover:border-amber-600" : "border-[#E6E4DD] bg-[#F4F2EB] text-neutral-300 line-through cursor-not-allowed"}`}
                          data-testid={`variant-${opt.name}-${value}`}>
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!product.requires_quote && (
          <div className="mt-6" data-testid="pdp-stock-status">
            {product.has_variants && !selectedVariant ? (
              <p className="text-sm text-neutral-500">Stok durumu için varyant seçin</p>
            ) : currentStock <= 0 ? (
              <p className="inline-flex items-center gap-2 text-sm font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-4 py-2">TÜKENDİ</p>
            ) : currentStock <= stockThreshold ? (
              <p className="inline-flex items-center gap-2 text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-4 py-2 animate-pulse-soft">
                <Zap size={14} /> Son {currentStock} adet — tükenmeden alın
              </p>
            ) : (
              <p className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-2">Stokta{product.shipping_badge ? ` — ${product.shipping_badge}` : ""}</p>
            )}
          </div>
          )}

          {product.customizable && !product.requires_quote && (
            <div className="mt-8 bg-white border-2 border-dashed border-amber-300 rounded-2xl p-5" data-testid="customization-panel">
              <p className="font-display font-bold text-base flex items-center gap-2"><ImagePlus size={17} className="text-amber-700" /> Kişiselleştirin</p>
              <p className="text-xs text-neutral-500 mt-1">Fotoğrafınızı yükleyin ve/veya ürüne yazılacak metni girin.</p>
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <label className={`h-11 px-5 rounded-full border-2 border-[#121214] text-sm font-semibold flex items-center gap-2 cursor-pointer hover:bg-[#121214] hover:text-white transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="custom-photo-label">
                    {uploading ? "Yükleniyor..." : "Fotoğraf Yükle"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleCustomUpload} data-testid="custom-photo-input" />
                  </label>
                  {customPhoto && (
                    <div className="relative">
                      <img src={customPhoto} alt="Yüklenen fotoğraf" className="w-14 h-14 rounded-xl object-cover border border-[#E6E4DD]" data-testid="custom-photo-preview" />
                      <button onClick={() => setCustomPhoto(null)} className="absolute -top-2 -right-2 w-5 h-5 bg-rose-600 text-white rounded-full text-[10px] flex items-center justify-center" data-testid="custom-photo-remove" aria-label="Fotoğrafı kaldır">×</button>
                    </div>
                  )}
                </div>
                <input
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value.slice(0, product.customization?.text_maxlen || 60))}
                  placeholder={product.customization?.text_label || "Ürüne yazılacak metin"}
                  className="w-full h-11 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600"
                  data-testid="custom-text-input"
                />
              </div>
            </div>
          )}

          {product.requires_quote ? (
            <div className="mt-8">
              <button onClick={() => setQuoteOpen(true)}
                className="w-full h-13 py-3.5 rounded-full bg-amber-600 text-white font-semibold hover:bg-amber-500 transition-colors flex items-center justify-center gap-2"
                data-testid="quote-request-btn">
                <FileText size={17} /> Teklif Al
              </button>
            </div>
          ) : (
          <div className="flex items-stretch gap-3 mt-8">
            <div className="flex items-center border-2 border-[#E6E4DD] rounded-full bg-white">
              <button className="px-4 py-3 disabled:opacity-30" disabled={qty <= 1} onClick={() => setQty(qty - 1)} data-testid="qty-minus"><Minus size={15} /></button>
              <span className="w-8 text-center font-bold" data-testid="qty-value">{qty}</span>
              <button className="px-4 py-3 disabled:opacity-30" disabled={qty >= currentStock} onClick={() => setQty(qty + 1)} data-testid="qty-plus"><Plus size={15} /></button>
            </div>
            <button onClick={() => handleAdd(false)} disabled={!canBuy}
              className="flex-1 h-13 py-3.5 rounded-full border-2 border-[#121214] font-semibold hover:bg-[#121214] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="add-to-cart-btn">
              <ShoppingBag size={17} /> Sepete Ekle
            </button>
            <button onClick={() => handleAdd(true)} disabled={!canBuy}
              className="flex-1 h-13 py-3.5 rounded-full bg-amber-600 text-white font-semibold hover:bg-amber-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="buy-now-btn">
              Hemen Al
            </button>
          </div>
          )}

          <div className="grid grid-cols-3 gap-3 mt-8 text-center">
            {[{ icon: Truck, t: product.shipping_badge || "Hızlı Kargo" }, { icon: ShieldCheck, t: "Güvenli Ödeme" }, { icon: RotateCcw, t: "14 Gün İade" }].map((x) => (
              <div key={x.t} className="bg-white border border-[#E6E4DD] rounded-xl py-3 px-2">
                <x.icon size={17} className="mx-auto text-amber-700 mb-1" />
                <p className="text-[11px] font-medium text-neutral-600">{x.t}</p>
              </div>
            ))}
          </div>
          {product.shipping_message && (
            <p className="text-xs text-neutral-500 mt-3 bg-white border border-[#E6E4DD] rounded-xl px-4 py-2.5" data-testid="pdp-shipping-message">{product.shipping_message}</p>
          )}

          <div className="mt-10 border-t border-[#E6E4DD] pt-8">
            <h2 className="font-display font-bold text-lg mb-3">Ürün Açıklaması</h2>
            <p className="text-sm text-neutral-600 leading-relaxed whitespace-pre-line" data-testid="pdp-description">{product.description}</p>
            <div className="grid grid-cols-2 gap-3 mt-6 text-xs">
              {[["Ürün Kodu", product.product_code], ["Barkod", product.barcode], ["KDV", `%${product.vat_rate}`], ["Desi", product.desi], ["Ağırlık", product.weight ? `${product.weight} kg` : null]]
                .filter(([, v]) => v != null && v !== "")
                .map(([k, v]) => (
                  <div key={k} className="bg-white border border-[#E6E4DD] rounded-lg p-3">
                    <p className="text-neutral-400">{k}</p><p className="font-semibold mt-0.5">{v}</p>
                  </div>
                ))}
            </div>
          </div>
        </motion.div>
      </div>

      {quoteOpen && product.requires_quote && (
        <QuoteModal product={product} onClose={() => setQuoteOpen(false)} />
      )}

      {product.related?.length > 0 && (
        <section className="mt-20">
          <Reveal><h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mb-8">Benzer Ürünler</h2></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {product.related.map((p, i) => <Reveal key={p.product_id} delay={i * 0.05}><ProductCard product={p} index={i} /></Reveal>)}
          </div>
        </section>
      )}
    </div>
  );
}
